import type { VerificationResponse } from '@/types';

const BASE = import.meta.env.VITE_API_URL || '/api/v1';
const API_PREFIX = '/api/v1';

export type SessionErrorCode = 'ALREADY_COMPLETED' | 'DEADLINE_EXCEEDED' | 'INVALID_STATUS';

/** Thrown when GET /questions returns ALREADY_COMPLETED (finished in another tab). */
export class TestCompletedElsewhereError extends Error {
  constructor() {
    super('Test completed elsewhere');
    this.name = 'TestCompletedElsewhereError';
  }
}

export class CompetitionSessionError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: SessionErrorCode,
  ) {
    super(message);
    this.name = 'CompetitionSessionError';
  }
}

async function parseError(res: Response): Promise<CompetitionSessionError> {
  const body = await res.json().catch(() => ({})) as {
    message?: string;
    code?: SessionErrorCode;
  };
  return new CompetitionSessionError(
    body.message ?? 'Błąd sesji turniejowej.',
    res.status,
    body.code,
  );
}

export async function verifyMagicLink(token: string): Promise<VerificationResponse> {
  const res = await fetch(`${BASE}/weryfikacja`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw await parseError(res);
  const body = await res.json() as { success: boolean; data: VerificationResponse };
  return body.data;
}

export async function restoreCompetitionSession(): Promise<VerificationResponse> {
  const res = await fetch(`${BASE}/sesja`, { credentials: 'include' });
  if (!res.ok) throw await parseError(res);
  const body = await res.json() as { success: boolean; data: VerificationResponse };
  return body.data;
}

export type SessionCheckState = 'active' | 'completed' | 'deadline' | 'denied';

export type SessionSnapshot =
  | { state: 'active'; data: VerificationResponse }
  | { state: 'completed' }
  | { state: 'deadline' }
  | { state: 'denied' };

/** Session probe: terminal states + active payload for multi-tab sync. */
export async function fetchSessionSnapshot(): Promise<SessionSnapshot> {
  const res = await fetch(`${BASE}/sesja`, { credentials: 'include' });
  if (res.ok) {
    const body = await res.json() as { success: boolean; data: VerificationResponse };
    return { state: 'active', data: body.data };
  }

  const body = await res.json().catch(() => ({})) as { code?: SessionErrorCode };
  if (body.code === 'ALREADY_COMPLETED') return { state: 'completed' };
  if (body.code === 'DEADLINE_EXCEEDED') return { state: 'deadline' };
  return { state: 'denied' };
}

/** @deprecated Use fetchSessionSnapshot */
export async function checkCompetitionSessionStatus(): Promise<SessionCheckState> {
  const snapshot = await fetchSessionSnapshot();
  return snapshot.state;
}

export async function persistProgress(
  answers: Record<string, string>,
  currentIndex: number,
): Promise<void> {
  const res = await competitionFetch('/api/v1/save-progress', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers, currentIndex }),
  });
  if (!res.ok) {
    throw new Error('Failed to save progress');
  }
}

export async function logoutCompetitionSession(): Promise<void> {
  await fetch(`${BASE}/sesja/wyloguj`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function buildCompetitionUrl(input: string): string {
  if (/^https?:\/\//.test(input)) return input;
  if (!input.startsWith(API_PREFIX)) return input;
  if (BASE === API_PREFIX) return input;
  const suffix = input.slice(API_PREFIX.length);
  return `${BASE}${suffix}`;
}

export function competitionFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(buildCompetitionUrl(input), { ...init, credentials: 'include' });
}
